import { TFunction } from 'next-i18next'
import { app } from '../../env'
import { AddressFieldSchema, DateOfBirthFieldSchema, FieldType, InputFieldSchema, Schema } from '@afterpaytouch/form'
import { ConsumerState } from '../../state'
import { GetSchemaAndFormDataResult, IdentityVerificationForm, SchemaUpdates } from './types'
import { nameMaxLengthValidation, namePattern, requiredValidation } from '../VerifyIdentity/validations'
import { SupportedLocale } from '../../model'
import { Country } from '@afterpay/types'
import { requiredAddress, validatePoBox } from '../ShippingAddressForm/validations'
import { getAddressOptionalFields, isConsumerLendingEligibleState } from '../../utils/profile-validation'
import { InputAddressData, InputAddressValidationRule } from '@afterpaytouch/core'
import { ALLOWED_ADDRESS_TYPES } from '../VerifyAddress/utils'
import { LogEvent, TrackingEvent } from '../../model/monitoring'
import { Ref } from 'react'

export const getInputFieldSchema = ({ value, label, error = false, options = {}, inline = false, otherProps = {} }): InputFieldSchema => ({
  type: FieldType.Input,
  show: true,
  props: {
    label,
    value,
    error,
    ...otherProps,
  },
  options,
  inline,
})

export const getInputBirthdaySchema = ({ locale, label, error = false, value, options, otherValidations = {}, otherProps = {} }): DateOfBirthFieldSchema => ({
  type: FieldType.DateOfBirth,
  show: true,
  props: {
    label,
    defaultValue: value,
    locale: locale as SupportedLocale,
    error,
    hasDateFormatLabel: false,
    ...otherProps,
  },
  options,
  ...otherValidations,
})

export const getAddressFieldSchema = ({ value, error = false, otherProps = {}, options = {}, disabled = false }): AddressFieldSchema => ({
  type: FieldType.Address,
  apiKey: app.GOOGLEMAPS_API_KEY,
  show: true,
  props: {
    initialAddress: value,
    error,
    disabled,
    ...otherProps,
  },
  options,
})

export const getFormData = (consumer: ConsumerState): IdentityVerificationForm => {
  return {
    givenNames: consumer.profile?.givenNames ?? '',
    otherGivenNames: consumer.profile?.otherGivenNames ?? '',
    surname: consumer.profile?.surname ?? '',
    dateOfBirth: consumer.profile?.dateOfBirth ?? '',
    address: {
      address1: consumer.profile?.contactAddress?.address1 ?? '',
      address2: consumer.profile?.contactAddress?.address2 ?? '',
      area2: consumer.profile?.contactAddress?.area2 ?? '',
      postcode: consumer.profile?.contactAddress?.postcode ?? '',
      state: consumer.profile?.contactAddress?.state ?? '',
      suburb: consumer.profile?.contactAddress?.suburb ?? '',
      countryCode: consumer.profile?.contactAddress?.countryCode ?? '',
    },
    partialNationalIdNumber: '',
    partialNationalIdNumberInfo: undefined,
    footer: undefined,
  }
}

export const getSchema = (
  consumer: ConsumerState,
  country: Country,
  components: any,
  t: TFunction,
  schemaUpdates: SchemaUpdates,
  error: boolean,
  logEvent: LogEvent,
  onAddressInputValueChange: () => void,
  iconClickHandler: () => void,
  iconRef?: Ref<HTMLElement>
): GetSchemaAndFormDataResult<IdentityVerificationForm> => {
  const initialFormData = getFormData(consumer)
  const { givenNames, otherGivenNames, surname, dateOfBirth, address, partialNationalIdNumber } = initialFormData
  const { accountCountry, preferredLocale } = consumer

  const required = requiredValidation(t)

  const { dobValidations } = schemaUpdates

  const namePatterns = namePattern(t)
  const nameMaxLengthOptions = nameMaxLengthValidation(t)

  const handleAddressCustomValidation = (parsedData: InputAddressData): string | null => {
    if (!isConsumerLendingEligibleState(parsedData)) {
      return t('consumerLending:identityVerification:formErrorLabels:ineligibleState')
    }

    return null
  }

  const schema: Schema<IdentityVerificationForm> = {
    givenNames: getInputFieldSchema({
      label: t('consumerLending:identityVerification:formLabels:givenNames'),
      value: givenNames,
      inline: true,
      options: {
        ...required('givenNames'),
        ...namePatterns('givenNames'),
        ...nameMaxLengthOptions('givenNames', accountCountry),
      },
      otherProps: {
        'data-dd-action-name': 'Identity Verification National Number Given Name Input',
      },
      error,
    }),
    otherGivenNames: getInputFieldSchema({
      label: t('consumerLending:identityVerification:formLabels:otherGivenNames'),
      value: otherGivenNames,
      inline: true,
      options: {
        ...namePatterns('otherGivenNames'),
        ...nameMaxLengthOptions('otherGivenNames', accountCountry),
      },
      otherProps: {
        'data-dd-action-name': 'Identity Verification National Number Other Given Name Input',
      },
      error,
    }),
    surname: getInputFieldSchema({
      label: t('consumerLending:identityVerification:formLabels:surname'),
      value: surname,
      options: {
        ...required('surname'),
        ...namePatterns('surname'),
        ...nameMaxLengthOptions('surname', accountCountry),
      },
      otherProps: {
        'data-dd-action-name': 'Identity Verification National Number Surname Input',
      },
      error,
    }),
    dateOfBirth: getInputBirthdaySchema({
      value: dateOfBirth,
      locale: preferredLocale,
      label: t('consumerLending:identityVerification:formLabels:dateOfBirth'),
      options: { ...required('dateOfBirth') },
      otherProps: {
        'data-dd-action-name': 'Identity Verification National Number Date Of Birth Input',
      },
      otherValidations: dobValidations,
      error,
    }),
    address: getAddressFieldSchema({
      value: address,
      otherProps: {
        label: t('consumerLending:identityVerification:formLabels:address'),
        manualAddressLabel: t('addNewAddress:findAddressLabelCaption'),
        onManualAddressLabelClick: () => logEvent(TrackingEvent.PRESSED_MANUAL_ADDRESS_TOGGLE),
        onManualAddressChange: onAddressInputValueChange,
        onInputValueChange: onAddressInputValueChange,
        address1Label: t('addNewAddress:address1Label'),
        address2Label: t('addNewAddress:address2Label'),
        postcodeLabel: t('addNewAddress:postcodeLabel'),
        componentRestrictions: { country },
        suburbLabel: t('addNewAddress:suburbLabel'),
        stateLabel: t('addNewAddress:stateLabel'),
        manualAddressLabelCaption: t('addNewAddress:findAddressLabelCaption'),
        optionalManualAddressFields: getAddressOptionalFields(country),
        allowedAddressType: ALLOWED_ADDRESS_TYPES,
        searchAddressLabel: t('addNewAddress:searchAddressLabel'),
        defaultExpanded: false,
        validateRule: InputAddressValidationRule.ByAddress,
        onCustomValidation: handleAddressCustomValidation,
        onManualModeChange: () => {},
        blurFieldDependencies: ['dateOfBirth'],
        'data-dd-action-name': 'Identity Verification National Number Address Input',
      },
      options: {
        validate: {
          ...requiredAddress({ name: 'address', country, t }),
          ...validatePoBox(t('addNewAddress:error:invalid', { context: 'poBoxError' })),
        },
      },
      error,
    }),
    partialNationalIdNumber: getInputFieldSchema({
      label: t('consumerLending:identityVerification:formLabels:partialNationalIdNumber'),
      value: partialNationalIdNumber,
      options: {
        maxLength: {
          value: 4,
          message: t('consumerLending:identityVerification:formErrorLabels:partialNationalIdNumberLength'),
        },
        minLength: {
          value: 4,
          message: t('consumerLending:identityVerification:formErrorLabels:partialNationalIdNumberLength'),
        },
        ...required('partialNationalIdNumber'),
      },
      otherProps: {
        format: '0'.repeat(4),
        inputMode: 'numeric',
        pattern: '[0-9]*',
        icon: 'Info',
        iconClickHandler,
        iconRef,
        'data-dd-action-name': 'Identity Verification National Number Partial National ID Number Input',
        'aria-describedby': 'partial-national-number-id-tooltip',
      },
      error,
    }),
    footer: {
      type: FieldType.Component,
      Component: components.footer,
    },
  }
  return { schema, initialFormData }
}
